import type {
	CAFlexAlign,
	CAFlexDirection,
	CAFlexJustify,
	CAFlexWrap,
} from '@ds/interfaces'
import { defineComponent, type PropType } from 'vue'

/**
	* Types used in `CAInput` component
	* @typedef {('row' | 'row-reverse' | 'column' | 'column-reverse')} FlexDirection - The direction of the content within the container.
	* @typedef {('around' | 'between' | 'center' | 'end' | 'start')} FlexJustify - The horizontal alignment of the content within the container.
	* @typedef {('nowrap' | 'wrap' | 'wrap-reverse')} FlexWrap - The wrapping of the content within the container.
	*
	* @component CAFlex
	* The `CAFlex` component is used establish a flex layout of content with the page.
	*
	* @prop {string} additionalClasses - The additional classes prop is used for adding additional classes to the flexbox itself.
	* @prop {boolean} bordered - Whether the flex container with have a styled border around the content.
	* @prop {FlexDirection} direction - The direction of the content within the flexbox. The types are: "row", "row-reverse", "column", "column-reverse".
	* @prop {FlexJustify} justify - The vertical alignment of the content within the flexbox. The types are: "around", "between", "center", "end", "start".
	* @prop {number} rows - The number of rows to display in the flexbox.
	* @prop {FlexWrap} wrap - The wrapping of the content within the flexbox. The types are: "nowrap", "wrap", "wrap-reverse".
	*
	*
	* @slot default - The default slot for the flex content.
	*
*/

export default defineComponent({
	name: 'CAFlex',

	props: {
		/**
		 * The additional classes prop is used for adding additional classes to the flexbox itself.
		 * @default ''
		 * @type {string}
		 */
		additionalClasses: { default: '', type: String },
		/**
		 * The alignment of the content within the flexbox.
		 * @default 'start'
		 * @type {CAFlexAlign}
		 * @enum {'baseline' | 'center' | 'end' | 'start' | 'stretch'}
		 */
		align: { default: 'start', type: String as PropType<CAFlexAlign> },
		/**
		 * Whether the flex container with have a styled border around the content.
		 * @default false
		 * @type {boolean}
		 */
		bordered: { default: false, type: Boolean },
		/**
		 * The direction of the content within the flexbox.
		 * @default 'row'
		 * @type {CAFlexDirection}
		 * @enum {'column' | 'column-reverse' | 'row' | 'row-reverse'}
		 */
		direction: { default: 'row', type: String as PropType<CAFlexDirection> },
		/**
		 * The horizontal alignment of the content within the flexbox.
		 * @default 'start'
		 * @type {CAFlexJustify}
		 * @enum {'around' | 'between' | 'center' | 'end' | 'start'}
		 */
		justify: { default: 'start', type: String as PropType<CAFlexJustify> },
		/**
		 * The spacing between items within the flexbox.
		 * @default '4'
		 * @type {string}
		 */
		spacing: { default: '4', type: String },
		/**
		 * The wrapping of the content within the flexbox.
		 * @default 'nowrap'
		 * @type {CAFlexWrap}
		 * @enum {'nowrap' | 'wrap' | 'wrap-reverse'}
		 */
		wrap: { default: 'nowrap', type: String as PropType<CAFlexWrap> },
	},

	computed: {
		flexClasses(): string {
			const useBorders = this.bordered ? 'p-5 border border-solid border-gray-400 rounded' : ''
			const isRow = this.direction !== 'column' && this.direction !== 'column-reverse'
			const setClasses = `
				${this.additionalClasses}
				flex-${this.direction}
				justify-${this.justify}
				items-${this.align}
				flex-${this.wrap}
				gap-${this.spacing}
			`
			const smallScreenClass = !isRow ? 'flex-col' : setClasses

			return `
				flex
				${useBorders}
				${smallScreenClass}
				ca-flex
				md:flex-${this.direction}
				md:justify-${this.justify}
				md:items-${this.align}
				md:flex-${this.wrap}
			`
		},
	},

	render() {
		return (
			<div class={this.flexClasses} data-test="ca-flex">
				{this.$slots.default?.()}
			</div>
		)
	},
})
